import { distributionPlanState } from "~/common/publishing/distribution-plan-state";
import type { BrowserTask } from "@ondit/publishing";
import type { DistributionPost } from "~/core/api/distribution";
import type { DistributionBrief, DistributionOperation } from "~/core/api/smart-distribution";
import { calendarPosts } from "./calendar-data";

export interface CalendarPlanEntry {
	id: string;
	briefId?: string;
	page: string;
	date: string | null;
	language: string;
	platforms: { id: string; name: string }[];
	state: string;
	legacy?: boolean;
}
/** Join by remote job identity, never by article title: one article can have many plans. */
export function calendarPlanEntries(
	briefs: DistributionBrief[],
	operations: DistributionOperation[],
	posts: DistributionPost[],
	tasks: BrowserTask[],
): CalendarPlanEntry[] {
	const publishing = operations.filter((o) => o.kind === "publish");
	const rows: CalendarPlanEntry[] = [];
	for (const brief of briefs) {
		const jobs = publishing.filter((o) => o.brief_id === brief.id);
		const dates = [...new Set(jobs.map((o) => o.scheduled_at ?? null))];
		if (!dates.length) dates.push(null);
		for (const date of dates) {
			const at = jobs.filter((o) => (o.scheduled_at ?? null) === date);
			const state = at.length
				? distributionPlanState({
						...brief,
						plan: {
							revision: brief.plan?.revision ?? brief.revision,
							items: at.map((o) => ({
								platform_id: o.platform_id ?? o.id,
								account_id: "",
								scheduled_at: o.scheduled_at ?? "",
							})),
						},
						jobs: at.map((o) => ({ platform_id: o.platform_id ?? o.id, job_id: o.id, status: { state: o.state } })),
					})
				: brief.operation?.state === "failed"
					? "failed"
					: distributionPlanState(brief);

			rows.push({
				id: `${brief.id}:${date ?? "unscheduled"}`,
				briefId: brief.id,
				page: brief.input.materials[0]?.id ?? "",
				date,
				language: brief.input.language,
				platforms: at.length
					? at.filter((o) => o.platform_id).map((o) => ({ id: o.platform_id!, name: o.platform_id! }))
					: (
							brief.channels ??
							brief.selected?.map((c) => ({
								platform_id: c.platform_id,
								platform_name: c.platform.name,
								format: c.format,
							})) ??
							[]
						).map((c) => ({ id: c.platform_id, name: c.platform_name })),
				state,
			});
		}
	}
	const knownBriefs = new Set(briefs.map((b) => b.id));
	for (const operation of publishing.filter((o) => !knownBriefs.has(o.brief_id))) {
		const date = operation.scheduled_at ?? null;
		const id = `${operation.brief_id}:${date ?? "unscheduled"}`;
		const existing = rows.find((row) => row.id === id);
		const platform = { id: operation.platform_id ?? "", name: operation.platform_id ?? "" };
		if (existing) existing.platforms.push(platform);
		else
			rows.push({
				id,
				briefId: operation.brief_id,
				page: operation.materials[0]?.id ?? "",
				date,
				language: operation.language,
				platforms: [platform],
				state: operation.state,
			});
	}
	const representedJobs = new Set(publishing.map((o) => o.id));
	for (const row of calendarPosts(posts, tasks)) {
		if (row.task?.pubzu_job_id && representedJobs.has(row.task.pubzu_job_id)) continue;
		rows.push({
			id: `legacy:${row.id}`,
			page: row.page,
			date: row.date,
			language: row.post?.locale ?? "",
			platforms: [{ id: row.platform, name: row.platform }],
			state: row.state,
			legacy: true,
		});
	}
	return rows;
}
export function calendarPlanParams(entry: CalendarPlanEntry): Record<string, string> {
	return {
		tab: "prepare",
		page: entry.page,
		...(entry.briefId ? { distribution_brief: entry.briefId } : {}),
		...(!entry.briefId && entry.date ? { scheduled_at: entry.date } : {}),
	};
}
